import { pluralizeTasks, pluralizeDays } from "../utils/plural";
import odznakaBrak from "../img/icons/odznaka_brak.png";
import odznakaBrazowa from "../img/icons/odznaka_brazowa.png";
import odznakaSrebrna from "../img/icons/odznaka_srebrna.png";
import odznakaZlota from "../img/icons/odznaka_zlota.png";

// --- STAŁE ---
export const AUTH_TOKEN_KEY = "token";
export const POPUP_DELAY = 1200;

// --- STYLE ---
export const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "white",
    borderColor: state.isFocused ? "#233855" : "#D1D5DB",
    borderRadius: "0.5rem",
    padding: "0.25rem",
    boxShadow: state.isFocused ? "0 0 0 1px #233855" : "none",
    "&:hover": {
      borderColor: "#233855",
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#233855",
    borderRadius: "0.375rem",
    color: "white",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "white",
    fontWeight: "400",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "white",
    "&:hover": {
      backgroundColor: "#8A0000",
      color: "white",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? "#233855"
      : state.isFocused
      ? "#F0F0F0"
      : "white",
    color: state.isSelected ? "white" : "#1F2937",
    fontWeight: "400",
    "&:active": {
      backgroundColor: "#233855",
    },
  }),
  groupHeading: (provided) => ({
    ...provided,
    color: "#8A0000",
    fontFamily: "Apotek",
    fontWeight: "600",
    textTransform: "uppercase",
    fontSize: "[0.5rem]",
    letterSpacing: "0.05em",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6B7280",
  }),
};

// --- LOGIKA ODZNAK ---
export const badgeConfig = [
  { level: "Brązowa", days: 1, image: odznakaBrazowa },
  { level: "Srebrna", days: 6, image: odznakaSrebrna },
  { level: "Złota", days: 31, image: odznakaZlota },
];

export const getMotivationalMessage = (status) => {
  if (!status) return "";
  const {
    problems_solved_today,
    problems_daily_goal,
    current_streak,
  } = status;
  const remainingTasks = problems_daily_goal - problems_solved_today;
  const isGoalCompleted = remainingTasks <= 0;
  if (isGoalCompleted) {
    if (current_streak > 0) {
      return "Cel zrobiony. Seria żyje.";
    }
    return "Cel zrobiony. Jeden dzień bliżej.";
  } else {
    if (current_streak > 0) {
      return `${remainingTasks} ${pluralizeTasks(remainingTasks)} do celu - seria ${current_streak} ${pluralizeDays(current_streak)} czeka.`;
    }
    return `${remainingTasks} ${pluralizeTasks(remainingTasks)} do celu. Co dziś - to na maturze.`;
  }
};

export const getBadgeStatus = (streak) => {
  let currentBadge = { image: odznakaBrak, textColor: null };
  if (streak >= badgeConfig[2].days) {
    currentBadge = { image: badgeConfig[2].image, textColor: "#B8860B" };
  } else if (streak >= badgeConfig[1].days) {
    currentBadge = { image: badgeConfig[1].image, textColor: "#606060" };
  } else if (streak >= badgeConfig[0].days) {
    currentBadge = { image: badgeConfig[0].image, textColor: "#8B4513" };
  }
  return currentBadge;
};

export const getNextBadgeInfo = (currentStreak) => {
  const nextBadge = badgeConfig.find((badge) => currentStreak < badge.days);
  if (!nextBadge) return null;
  return {
    level: nextBadge.level,
    totalDays: nextBadge.days,
    remainingDays: nextBadge.days - currentStreak,
  };
};

const getMaturaDays = () => {
  let maturaDate = new Date('2027-05-05');
  try {
    const raw = localStorage.getItem('global_config');
    if (raw) {
      const cfg = JSON.parse(raw);
      if (cfg?.content_variables?.matura_timestamp_utc)
        maturaDate = new Date(cfg.content_variables.matura_timestamp_utc * 1000);
    }
  } catch (e) {}
  maturaDate.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((maturaDate - today) / (1000 * 60 * 60 * 24)));
};

// Rotuje co ~6 dni — 5 różnych komunikatów w miesiącu
const msgIdx = () => Math.floor(new Date().getDate() / 6) % 5;

export const getDashboardSubtitle = (status, totalProblems = 0) => {
  if (!status) return "Co robisz dziś - liczy się na maturze.";

  const { problems_solved_today: solved, problems_daily_goal: goal } = status;
  const hour = new Date().getHours();
  const remaining = goal - solved;
  const goalDone = remaining <= 0;
  const dtm = getMaturaDays();
  const i = msgIdx();

  // --- Stany specjalne ---
  if (totalProblems === 0 && solved === 0)
    return "Zacznij od pierwszego zadania. Reszta będzie łatwiejsza.";

  if (dtm === 0)
    return "Dziś matura. Zaufaj temu co umiesz.";

  // --- Nocne/poranne override ---
  if (hour >= 22 || hour < 5)
    return "Kiedy inni śpią, Ty robisz matmę. Szacunek.";
  if (hour >= 5 && hour < 8)
    return "Matma przed szkołą? Przynajmniej nikt nie będzie przeszkadzał.";

  // --- Cel dzienny zrobiony ---
  if (goalDone) return [
    "Cel zrobiony. Krok po kroku do matury.",
    "Cel dzienny zaliczony. Regularność ma znaczenie.",
    "Dzisiejsza porcja matematyki: zrobiona. Kolejny dzień zaliczony.",
    "Cel na dziś: zamknięty. Wytrwałóść ma sens.",
    `Cel zaliczony. ${dtm} dni do matury - trzymaj kurs.`,
  ][i];

  // --- Matura bardzo blisko ---
  if (dtm <= 14)
    return `${dtm} ${pluralizeDays(dtm)} do matury. Dziś też się liczy.`;

  // --- Prawie cel ---
  if (remaining === 1)
    return "Jedno zadanie do celu dziennego. Serio, jedno.";

  // --- W trakcie ---
  if (solved > 0)
    return `${solved} ${pluralizeTasks(solved)} z ${goal} za tobą. Dobij do końca celu dziennego.`;

  // --- Nie zaczął — rotacja ---
  return [
    `Do matury: ${dtm} dni. Zrób swój cel na dziś.`,
    "Matematyka nie lubi przerw. Zacznij teraz, skończ szybko.",
    "Nie musisz siedzieć godzinami - zrób swoje na dziś i wystarczy.",
    "Każde zadanie to jedno mniej, które może cię zaskoczyć na maturze.",
    "Najlepszy moment żeby zacząć był miesiąc temu. Drugi najlepszy jest teraz.",
  ][i];
};

// Eksportujemy też odznakę "Brak" na wypadek stanu początkowego
export { odznakaBrak };